import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import CourseCard from "../../../../components/Course/CourseCard";
import LoadingSpinner from "../../../../components/UI/LoadingSpinner";
import CreateSectionModal from "../../../TutorPage/Dashboard/components/CreateSectionModal";
import type { ClassPageHookReturn } from "../hooks/useClassPage";
import * as S from "../styles";
import type { SortType } from "../types";

export default function ClassPageView(d: ClassPageHookReturn) {
	if (d.loading) {
		return (
			<S.ClassPageContainer>
				<Header onUserNameClick={() => {}} />
				<S.LoadingContainer>
					<LoadingSpinner />
					<p>수강 중인 강의를 불러오는 중...</p>
				</S.LoadingContainer>
				<Footer />
			</S.ClassPageContainer>
		);
	}

	if (d.error) {
		return (
			<S.ClassPageContainer>
				<Header onUserNameClick={() => {}} />
				<S.ErrorContainer>
					<S.ErrorMessage>{d.error}</S.ErrorMessage>
					<S.RetryButton onClick={() => window.location.reload()}>
						다시 시도
					</S.RetryButton>
				</S.ErrorContainer>
				<Footer />
			</S.ClassPageContainer>
		);
	}

	return (
		<S.ClassPageContainer>
			<Header onUserNameClick={() => {}} />
			<S.ContentSection>
				<S.PageTitle>내 강의실</S.PageTitle>
				<S.TabNavigation>
					<S.Tab active={d.activeTab === "all"} onClick={() => d.setActiveTab("all")}>
						참여한 수업 목록 ({d.stats.all})
					</S.Tab>
					<S.Tab
						active={d.activeTab === "in-progress"}
						onClick={() => d.setActiveTab("in-progress")}
					>
						관리 중인 수업 목록 ({d.stats.inProgress})
					</S.Tab>
					<S.Tab
						active={d.activeTab === "completed"}
						onClick={() => d.setActiveTab("completed")}
					>
						공개된 클래스 ({d.stats.completed})
					</S.Tab>
					<S.EnrollButton onClick={() => d.setShowEnrollModal(true)}>
						수업 참가
					</S.EnrollButton>
					<S.CreateCourseButton
						onClick={() => d.createSection.setShowCreateModal(true)}
					>
						수업 만들기
					</S.CreateCourseButton>
					{d.activeTab === "in-progress" && (
						<S.AdminPageButton onClick={() => d.navigate("/tutor")}>
							관리페이지
						</S.AdminPageButton>
					)}
				</S.TabNavigation>
				<S.SearchAndSort>
					<S.SearchBar>
						<S.SearchInput
							type="text"
							placeholder="강의명 검색"
							value={d.searchTerm}
							onChange={(e) => d.setSearchTerm(e.target.value)}
						/>
						<S.SearchIcon>🔍</S.SearchIcon>
					</S.SearchBar>
					<S.SortSelect
						value={d.sortBy}
						onChange={(e) => d.setSortBy(e.target.value as SortType)}
					>
						<option value="recent">최근 개설일 순</option>
						<option value="name">강의명 순</option>
					</S.SortSelect>
				</S.SearchAndSort>
				{d.filteredSections.length === 0 ? (
					<S.EmptyState>
						<p>
							{d.activeTab === "all" && "참여한 수업이 없습니다."}
							{d.activeTab === "in-progress" && "관리 중인 수업이 없습니다."}
							{d.activeTab === "completed" && "공개된 클래스가 없습니다."}
						</p>
					</S.EmptyState>
				) : (
					<S.CoursesGrid>
						{d.filteredSections.map((course) => (
							<CourseCard
								key={course.id}
								course={course}
								onStatusUpdate={d.handleStatusUpdate}
								hideBatch
								showEnrollButton={
									d.activeTab === "completed" && !!course.enrollmentCode
								}
								onEnroll={
									d.activeTab === "completed" && course.enrollmentCode
										? () => {
												d.setEnrollmentCode(course.enrollmentCode ?? "");
												d.setShowEnrollModal(true);
											}
										: undefined
								}
								overrideLinkPath={d.getCourseLinkPath(course)}
							/>
						))}
					</S.CoursesGrid>
				)}
			</S.ContentSection>
			{d.showEnrollModal && (
				<S.ModalOverlay onClick={() => d.setShowEnrollModal(false)}>
					<S.ModalContent onClick={(e) => e.stopPropagation()}>
						<S.ModalHeader>
							<h2>수업 참가</h2>
							<S.CloseButton onClick={() => d.setShowEnrollModal(false)}>
								×
							</S.CloseButton>
						</S.ModalHeader>
						<S.ModalBody>
							<label>참가 코드 또는 링크</label>
							<input
								type="text"
								className="enroll-input"
								placeholder={`예: ABCD1234 또는 ${window.location.origin}/enroll/ABCD1234`}
								value={d.enrollmentCode}
								onChange={(e) => d.setEnrollmentCode(e.target.value)}
							/>
							<p className="enroll-help-text">
								참가 코드만 입력하거나 전체 링크를 붙여넣으세요.
							</p>
						</S.ModalBody>
						<S.ModalActions>
							<S.CancelButton onClick={() => d.setShowEnrollModal(false)}>
								취소
							</S.CancelButton>
							<S.EnrollSubmitButton
								onClick={async () => {
									await d.handleEnrollByCode();
									d.setShowEnrollModal(false);
								}}
								disabled={d.enrollLoading}
							>
								{d.enrollLoading ? "처리 중..." : "참가하기"}
							</S.EnrollSubmitButton>
						</S.ModalActions>
					</S.ModalContent>
				</S.ModalOverlay>
			)}
			<CreateSectionModal
				isOpen={d.createSection.showCreateModal}
				onClose={() => d.createSection.setShowCreateModal(false)}
				formData={d.createSection.formData}
				setFormData={d.createSection.setFormData}
				onSubmit={d.createSection.handleCreateSection}
			/>
			{d.createSection.isCreatingSection && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						backgroundColor: "rgba(0,0,0,0.35)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 10000,
					}}
				>
					<div
						style={{
							background: "white",
							padding: "1.5rem 2rem",
							borderRadius: "12px",
							boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
						}}
					>
						<LoadingSpinner message="수업 만들기..." />
					</div>
				</div>
			)}
			<Footer />
		</S.ClassPageContainer>
	);
}
